import React, { useEffect, useState } from "react";
import { Spin, Alert, DatePicker, Select, Button, Space, message } from "antd";
import ExecutiveReportTable from "./ExecutiveReportTable";
import { getWellnessExecutiveReport, fetch_all_labs } from "../../endpoints/API";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Breakpoint hook
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
  };
}

function WellnessExecutiveReport() {
  const { isMobile, isTablet } = useBreakpoint();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labs, setLabs] = useState([]);
  const [selectedLab, setSelectedLab] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ]);

  const fetchLabs = async () => {
    try {
      const res = await fetch_all_labs();
      setLabs(res?.data.labs || []);
    } catch (err) {
      message.error(err?.response?.data?.error || "failed to fetch lab");
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = {
        from_date: dateRange?.[0]?.format("YYYY-MM-DD"),
        to_date: dateRange?.[1]?.format("YYYY-MM-DD"),
        lab: selectedLab || "",
      };
      const res = await getWellnessExecutiveReport(params);
      setReportData(res?.data?.data);
    } catch (err) {
      setError("Failed to fetch report data.");
      message.error(err?.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
    fetchReportData();
  }, []);

  const handleSearch = () => {
    fetchReportData();
  };

  const handleDownload = () => {
    if (!reportData || reportData.length === 0) return;
    const rows = [];

    reportData.forEach((entry) => {
      const { lab_name, employee_name, date, total_km, segments } = entry;
      if (segments && Array.isArray(segments)) {
        segments.forEach((segment) => {
          rows.push({
            Lab: lab_name,
            Employee: employee_name,
            Date: date,
            "Total KM": total_km,
            "Start KM": segment.start_km,
            "End KM": segment.end_km,
            "Start Time": formatTime(segment.start_time),
            "End Time": formatTime(segment.end_time),
            "Total Pouches": segment.inventory?.total_pouches ?? "",
            "Total Vaccutainers": segment.inventory?.total_vaccutainers ?? "",
            "Total Sterile Vaccutainers": segment.inventory?.total_sterile_vaccutainers ?? "",
            "Total Urine Vaccutainers": segment.inventory?.total_urine_vaccutainers ?? "",
            "Total Tubes": segment.inventory?.total_tubes ?? "",
          });
        });
      }
    });

    if (rows.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DailyReport");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Daily_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  function formatTime(timeString) {
    if (!timeString) return "";
    const [h, m, s] = timeString.split(":");
    return `${h}:${m}:${s?.split(".")[0]}`;
  }

  return (
    <div>
      {/* ── Filter bar — stacks vertically on mobile ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 16,
          alignItems: "flex-start",
        }}
      >
        <Select
          placeholder="Select Lab"
          style={{ width: isMobile ? "100%" : 200 }}
          onChange={(value) => setSelectedLab(value)}
          allowClear
        >
          {labs.map((lab) => (
            <Option key={lab.id} value={lab.id}>
              {lab.name}
            </Option>
          ))}
        </Select>

        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          style={{ width: isMobile ? "100%" : "auto" }}
          // On very small screens use a compact picker
          size={isMobile ? "middle" : "middle"}
        />

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          Search
        </Button>

        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          {isMobile ? "Download" : "Download Report"}
        </Button>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Loading report..." />
        </div>
      ) : error ? (
        <Alert message={error} type="error" />
      ) : (
        <ExecutiveReportTable data={reportData} />
      )}
    </div>
  );
}

export default WellnessExecutiveReport;